import React, { useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useTokenMonitor } from "@/hooks/useTokenMonitor";
import { Loader2 } from "lucide-react";
import { logger } from "@/utils/logger";

interface AuthInitializerProps {
  children: React.ReactNode;
}

const AuthInitializer: React.FC<AuthInitializerProps> = ({ children }) => {
  const { isCheckingAuth, isAuthenticated, revalidateAuth } = useAuth();

  // Ativar monitoramento de token
  useTokenMonitor();

  useEffect(() => {
    // Configurar listener para eventos de storage (para sincronizar entre abas)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "hasValidToken") {
        logger.info("🔄 Token alterado em outra aba, revalidando...");
        revalidateAuth();
      }
    };

    window.addEventListener("storage", handleStorageChange);

    // Listener para eventos de conexão de rede
    const handleOnline = () => {
      logger.info("🌐 Conexão restaurada, verificando autenticação...");
      if (isAuthenticated) {
        revalidateAuth();
      }
    };

    const handleOffline = () => {
      logger.warn("📡 Conexão perdida");
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [isAuthenticated, revalidateAuth]);

  // Mostrar loading durante verificação inicial
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Verificando autenticação...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default AuthInitializer;
